// Buyer Order View: the payment method of an order already placed, its real
// payment status, and "Change Payment Method" while the Seller/Provider has
// not decided. The buyer authorized the payment at checkout; nothing here
// re-confirms the purchase or accepts the payment in place of the
// Seller/Provider. Genuine pending steps (bank authentication, a PayPal
// approval that never happened) keep their own explicit action.
import React, { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { FaCreditCard, FaPaypal } from 'react-icons/fa';
import { REACT_PK } from '../../../config';
import { confirmOrderPaymentAPI, syncStripeOrderAPI, getAvailablePaymentMethodsAPI, authorizePayPalOrderAPI } from '../../auth/paymentsApi';
import PayPalCheckoutButton from './PayPalCheckoutButton';
import ChangePaymentMethodModal from './ChangePaymentMethodModal';
import { orderText as t } from './orderManagementText';
import { acceptanceActor, brandName, errorKey, expiryText, paymentMethodChange, paymentStage, shortCard } from './orderPaymentMethodState';
import './OrderPaymentMethod.css';

const stripePromise = REACT_PK ? loadStripe(REACT_PK) : null;

const STAGES = {
  awaiting: { label: actor => (actor === 'provider' ? 'stAwaitingProvider' : 'stAwaitingSeller'), hint: 'hintAwaiting', tone: 'info' },
  method_required: { label: () => 'stMethodRequired', hint: 'hintMethodRequired', tone: 'warn' },
  paypal_approval: { label: () => 'stPayPalApproval', hint: 'hintPayPal', tone: 'warn' },
  bank_action: { label: () => 'stBankAction', hint: 'hintBankAction', tone: 'warn' },
  authorized: { label: () => 'stAuthorized', hint: 'hintAuthorized', tone: 'info' },
  failed: { label: () => 'stFailed', hint: 'hintFailed', tone: 'error' },
  processing: { label: () => 'stProcessing', tone: 'info' },
  captured: { label: () => 'stCaptured', tone: 'ok' },
  canceled: { label: () => 'stCanceled', tone: 'muted' },
};

// OrdersView keeps the card it already reads from Stripe as b_* fields.
const toCard = card => (card?.b_last4 ? { id: card.PM_id_sel || '', brand: card.b_brand, last4: card.b_last4, exp_month: card.b_exp_month, exp_year: card.b_exp_year } : null);

const Panel = ({ orderId, productId, line, card, title, readOnly = false, autoOpenChange = false, onAutoOpenHandled, onChanged }) => {
  const stripe = useStripe();
  const [modal, setModal] = useState(false);
  const [preferNewest, setPreferNewest] = useState(false);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState('');
  const inFlight = useRef(false);
  const lineId = line?._id;
  const stage = paymentStage(line);
  const change = paymentMethodChange(line);
  const paypal = line?.payment?.provider === 'paypal';
  const current = toCard(card);
  const canAct = !readOnly && Boolean(orderId && lineId);

  // Back from the wallet's "Add Card": reopen the change dialog once the
  // order is loaded, with the new card preselected.
  useEffect(() => {
    if (!autoOpenChange || !lineId) return;
    if (canAct && change.allowed) { setPreferNewest(true); setModal(true); }
    onAutoOpenHandled?.();
  }, [autoOpenChange, lineId, canAct, change.allowed, onAutoOpenHandled]);

  useEffect(() => {
    if (stage !== 'paypal_approval' || !canAct) return undefined;
    let active = true;
    getAvailablePaymentMethodsAPI().then(data => { if (active) setPaypalClientId(data?.paypalClientId || ''); }).catch(() => {});
    return () => { active = false; };
  }, [stage, canAct]);

  if (!current && !stage && !paypal) return null;

  const run = async action => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setNotice(null);
    try { await action(); }
    catch (e) { setNotice({ type: 'error', text: e.code ? t(errorKey(e)) : e.message || t('errGeneric') }); }
    finally { inFlight.current = false; setBusy(false); }
  };
  // Existing bank step of this order's payment, same card, nothing captured.
  const completeBankStep = () => run(async () => {
    const result = await confirmOrderPaymentAPI(orderId, lineId);
    if (result.status === 'requires_action' && result.clientSecret) {
      if (!stripe) throw new Error(t('pmBankAuthFailed'));
      const confirmation = await stripe.confirmCardPayment(result.clientSecret);
      await syncStripeOrderAPI(orderId).catch(() => {});
      if (confirmation.error) { await onChanged?.(); throw new Error(t('pmBankAuthFailed')); }
    }
    await onChanged?.();
    setNotice({ type: 'success', text: t('pmBankAuthDone') });
  });
  const handleUpdated = async (result, { keepOpen = false } = {}) => {
    await onChanged?.();
    if (keepOpen || !result) return;
    setModal(false); setPreferNewest(false);
    setNotice({ type: 'success', text: t('pmUpdated').replace('{card}', result.paymentMethod ? shortCard(result.paymentMethod) : '') });
  };

  const meta = STAGES[stage];
  const returnUrl = `/c/orders?oid=${encodeURIComponent(orderId || '')}&pid=${encodeURIComponent(productId || '')}&opid=${encodeURIComponent(lineId || '')}&changePayment=1`;
  const hintId = `opm-hint-${lineId}`;
  return <section className="opm" aria-labelledby={`opm-title-${lineId}`} aria-busy={busy}>
    <h5 className="ov-section-title opm-title" id={`opm-title-${lineId}`}>{title || t('pmTitle')}</h5>
    <div className="ov-card-info opm-box">
      <p className="opm-card">
        <span className="opm-card__icon" aria-hidden="true">{paypal ? <FaPaypal /> : <FaCreditCard />}</span>
        <span>
          <strong>{paypal ? t('pmPayPal') : current ? t('pmCardEnding').replace('{brand}', brandName(current.brand)).replace('{last4}', current.last4) : t('pmNone')}</strong>
          {!paypal && expiryText(current) && <span className="opm-card__exp">{t('pmExpires').replace('{date}', expiryText(current))}</span>}
        </span>
      </p>
      {meta && <dl className="opm-details">
        <div><dt>{t('pmStatus')}</dt><dd><span className={`opm-badge opm-badge--${meta.tone}`} data-testid="order-payment-status">{t(meta.label(acceptanceActor(line)))}</span></dd></div>
      </dl>}
      {meta?.hint && <p className="opm-hint" id={hintId}>{t(meta.hint)}</p>}
      {notice && <div role={notice.type === 'error' ? 'alert' : 'status'} className={`opm-notice opm-notice--${notice.type}`}>{notice.text}</div>}
      {canAct && <div className="opm-actions">
        {stage === 'bank_action' && <button type="button" className="ov-btn ov-btn-primary opm-no-pulse" disabled={busy} onClick={completeBankStep}>{busy ? t('processing') : t('pmBankAuth')}</button>}
        {change.visible && <button type="button" className={`ov-btn ${['failed', 'method_required'].includes(stage) ? 'ov-btn-primary opm-no-pulse' : 'ov-btn-outline'}`} disabled={busy || !change.allowed} aria-describedby={meta?.hint ? hintId : undefined} onClick={() => { setPreferNewest(false); setNotice(null); setModal(true); }}>{t('pmChange')}</button>}
        {stage === 'paypal_approval' && paypalClientId && <PayPalCheckoutButton clientId={paypalClientId} disabled={busy}
          createElakayOrder={async () => (await confirmOrderPaymentAPI(orderId, lineId))?.paypal?.paypalOrderId}
          approve={async id => { try { await authorizePayPalOrderAPI(id); await onChanged?.(); } catch (e) { setNotice({ type: 'error', text: e.message || t('errGeneric') }); } }}
          cancel={() => onChanged?.()} onError={() => setNotice({ type: 'error', text: t('errGeneric') })} />}
      </div>}
    </div>
    {canAct && change.visible && <ChangePaymentMethodModal show={modal} onHide={() => setModal(false)} orderId={orderId} line={line} currentCard={current}
      addCardUrl={`/wallet?addCard=true&returnUrl=${encodeURIComponent(returnUrl)}`} preferNewest={preferNewest} onUpdated={handleUpdated} />}
  </section>;
};

const OrderPaymentMethod = props => <Elements stripe={stripePromise}><Panel {...props} /></Elements>;
export default OrderPaymentMethod;
